/**
 * Preview overlay (plan 6.J) — static lookalike of the public booking page.
 *
 * Renders instantly from builder state (name, duration, venue, timezone, price, schedule).
 * Does not load the live ShareEvent app in an iframe (slow + empty space). “Open” still
 * opens the real booking URL in a new tab when the event is published and active.
 */

import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import octoIcon from "../../../../assets/icons/octo_icon.svg";
import { FRONTEND_BASE_URL, IMAGE_BASE_URL } from "../../../../utils/utility";
import Icon from "../ui/Icon";
import { currentBusinessName, useBuilder } from "../state/builderContext";
import { actions } from "../state/builderReducer";
import { E } from "../state/constants";
import {
  formatTime,
  isGroup,
  isPaid,
  priceLabel,
  timezoneLabel,
  venueName,
  weeklyRows,
  durationMinutes,
  inviteeChoosesDuration,
} from "../state/selectors";

const SLOT_COUNT = 12;

/** Next date whose weekday has an enabled weekly row + slots from its first interval. */
export const nextAvailable = (rows, increment, duration) => {
  const enabled = (rows || []).filter((r) => (r.schedules || []).length);
  if (!enabled.length) return null;
  const step = parseInt(increment, 10) || 15; // BookingController fallback
  for (let i = 0; i < 14; i++) {
    const day = moment().add(i, "days");
    const isoDow = day.isoWeekday(); // 1 = Monday … 7 = Sunday (weekday_id)
    const row = enabled.find((r) => parseInt(r.dayId, 10) === isoDow);
    if (!row) continue;
    const first = row.schedules[0];
    const start = moment(first.start_time, "HH:mm");
    const end = moment(first.end_time, "HH:mm");
    if (!start.isValid() || !end.isValid()) continue;
    const slots = [];
    const cursor = start.clone();
    while (cursor.clone().add(duration || step, "minutes").isSameOrBefore(end) && slots.length < SLOT_COUNT) {
      slots.push(cursor.format("HH:mm"));
      cursor.add(step, "minutes");
    }
    if (!slots.length) continue;
    return { date: day, slots };
  }
  return null;
};

const weekdayCells = (month) => {
  const start = month.clone().startOf("month");
  const end = month.clone().endOf("month");
  const gridStart = start.clone().startOf("isoWeek");
  const gridEnd = end.clone().endOf("isoWeek");
  const cells = [];
  const cursor = gridStart.clone();
  while (cursor.isSameOrBefore(gridEnd, "day")) {
    cells.push(cursor.clone());
    cursor.add(1, "day");
  }
  return cells;
};

/** Static ShareEvent-style booking page fed with live form values — no network. */
const BookingPagePreview = ({ state, timeFormat }) => {
  const paid = isPaid(state);
  const name = state.type.event_name || "Untitled event";
  const business = currentBusinessName();
  const durationText = inviteeChoosesDuration(state)
    ? "Invitee chooses"
    : `${durationMinutes(state) || 30} minutes`;
  const formatText = isGroup(state) ? `Group · Up to ${state.type.no_of_attendies || 2}` : "One to One";
  const venue = venueName(state) || "Location to be set";
  const tz = timezoneLabel(state) || "Time zone to be set";
  const price = paid ? priceLabel(state) || "—" : "Free";
  const next = useMemo(
    () => nextAvailable(weeklyRows(state), state.availability.slot_duration_increment_of, durationMinutes(state)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.availability.schedules, state.availability.slot_duration_increment_of, state.availability.event_duration_in_minutes]
  );
  const [month, setMonth] = useState(() => (next ? next.date.clone().startOf("month") : moment().startOf("month")));
  const selectedDay = next ? next.date : null;
  const selectedSlot = next && next.slots.length ? next.slots[0] : null;
  const imageUrl = state.details.event_image_url
    ? `${IMAGE_BASE_URL}${state.details.event_image_url}`
    : "";
  const cells = weekdayCells(month);
  const accent = state.details.event_color || "#0c4767";

  return (
    <div className="eb-bp-preview" style={{ ["--eb-bp-accent"]: accent }}>
      <div className="eb-bp-topbar">
        <img src={octoIcon} alt="" />
        <strong>
          Meet<span>Octo</span>
        </strong>
      </div>
      <div className="eb-bp-body">
        <aside className="eb-bp-sidebar">
          {imageUrl ? <img className="eb-bp-thumb" src={imageUrl} alt="" /> : <div className="eb-bp-thumb eb-bp-thumb-empty" />}
          <strong className="eb-bp-company">{business || "Your business"}</strong>
          <div className="eb-bp-you">
            <span>YOU ARE BOOKING</span>
            <h2>{name}</h2>
            <ul>
              <li>
                <Icon name="clock" size={14} />
                {durationText}
              </li>
              <li>
                <Icon name="pin" size={14} />
                {venue}
              </li>
              <li>
                <Icon name="users" size={14} />
                {formatText}
              </li>
              <li>
                <span className={`eb-bp-price${paid ? "" : " free"}`}>{price}</span>
              </li>
            </ul>
          </div>
        </aside>
        <section className="eb-bp-main">
          <div className="eb-bp-panel">
            <span className="eb-bp-label">SELECT DATE</span>
            <div className="eb-bp-calendar">
              <header>
                <button type="button" aria-label="Previous month" onClick={() => setMonth((m) => m.clone().subtract(1, "month"))}>
                  ‹
                </button>
                <strong>{month.format("MMMM YYYY")}</strong>
                <button type="button" aria-label="Next month" onClick={() => setMonth((m) => m.clone().add(1, "month"))}>
                  ›
                </button>
              </header>
              <div className="eb-bp-dow">
                {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
                  <span key={d}>{d}</span>
                ))}
              </div>
              <div className="eb-bp-days">
                {cells.map((day) => {
                  const inMonth = day.month() === month.month();
                  const isSelected = selectedDay && day.isSame(selectedDay, "day");
                  const hasHours =
                    inMonth &&
                    weeklyRows(state).some(
                      (r) => parseInt(r.dayId, 10) === day.isoWeekday() && (r.schedules || []).length
                    );
                  return (
                    <span
                      key={day.format("YYYY-MM-DD")}
                      className={[!inMonth ? "muted" : "", isSelected ? "selected" : "", hasHours && !isSelected ? "has-hours" : ""]
                        .filter(Boolean)
                        .join(" ")}
                    >
                      {day.date()}
                    </span>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="eb-bp-panel eb-bp-times-panel">
            <span className="eb-bp-label">SELECT TIME</span>
            <label className="eb-bp-tz">
              <select disabled value={tz} aria-label="Timezone">
                <option>{tz}</option>
              </select>
            </label>
            <div className="eb-bp-slots">
              {next ? (
                next.slots.map((s) => (
                  <button type="button" key={s} className={s === selectedSlot ? "selected" : ""} tabIndex={-1}>
                    {formatTime(s, timeFormat)}
                  </button>
                ))
              ) : (
                <em>Add availability to see times</em>
              )}
            </div>
            <button type="button" className="eb-bp-confirm" tabIndex={-1}>
              {selectedSlot ? `Confirm ${formatTime(selectedSlot, timeFormat)}` : "Select a time"}
            </button>
          </div>
        </section>
      </div>
    </div>
  );
};

const PreviewOverlay = () => {
  const { state, dispatch, businessId } = useBuilder();
  const open = state.ui.previewOpen;
  const close = () => dispatch(actions.ui({ previewOpen: false }));
  const eventUrl = String(state.type.event_url || "").trim();
  const liveUrl = businessId && eventUrl ? `${FRONTEND_BASE_URL}booking/${businessId}/${eventUrl}` : "";
  const canOpenLive = Boolean(liveUrl && state.meta.isDraft !== E && state.meta.isActive === E);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && close();
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const barUrl = liveUrl || `${FRONTEND_BASE_URL}booking/${businessId || ""}/${eventUrl || ""}`;

  return (
    <div
      className="eb-preview-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Booking page preview"
      onMouseDown={(e) => e.target === e.currentTarget && close()}
    >
      <div className="eb-preview-window eb-preview-window-live">
        <div className="eb-preview-browser">
          <div className="eb-browser-bar">
            <button
              type="button"
              className="eb-browser-dot eb-browser-dot-close"
              aria-label="Close preview"
              title="Close"
              onClick={close}
            />
            <i className="eb-browser-dot" aria-hidden="true" />
            <i className="eb-browser-dot" aria-hidden="true" />
            <span className="eb-browser-url">{barUrl}</span>
            {canOpenLive ? (
              <a className="eb-browser-open" href={liveUrl} target="_blank" rel="noopener noreferrer">
                Open
              </a>
            ) : null}
            <button type="button" className="eb-preview-close" aria-label="Close preview" onClick={close}>
              ×
            </button>
          </div>
          <BookingPagePreview state={state} timeFormat={(state.lookups.eventConfig || {}).time_format} />
        </div>
      </div>
    </div>
  );
};

export default PreviewOverlay;
